import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { rtlLanguages } from '../i18n';
import './Layout.css';

interface Language {
  code: string;
  label: string;
  name: string;
}

const languages: Language[] = [
  { code: 'de', label: 'DE', name: 'Deutsch' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'zh', label: '中', name: '中文' },
  { code: 'hi', label: 'हि', name: 'हिन्दी' },
  { code: 'ru', label: 'RU', name: 'Русский' },
  { code: 'ja', label: '日', name: '日本語' },
  { code: 'ar', label: 'AR', name: 'العربية' }
];

type ModalType = 'imprint' | 'privacy' | null;

export default function Layout() {
  const { t, i18n } = useTranslation();
  const [modal, setModal] = useState<ModalType>(null);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const isRtl = rtlLanguages.includes(i18n.language?.split('-')[0] ?? '');
  const currentLang = i18n.language?.split('-')[0] || 'en';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
      if (projectsRef.current && !projectsRef.current.contains(e.target as Node)) {
        setIsProjectsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProjectsOpen(false);
  };

  const toggleProjects = () => {
    setIsProjectsOpen(!isProjectsOpen);
  };

  return (
    <div className="layout" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="header">
        {/* Hamburger Button - Mobile only */}
        <button
          className="hamburger"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Menu"
        >
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <NavLink
            to="/"
            className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
            end
            onClick={closeMenu}
          >
            {t('nav.home')}
          </NavLink>

          {/* Projects Dropdown */}
          <div
            className={`nav-dropdown ${isProjectsOpen ? 'nav-dropdown-open' : ''}`}
            ref={projectsRef}
          >
            <button
              className="nav-dropdown-trigger"
              onClick={toggleProjects}
              aria-expanded={isProjectsOpen}
            >
              {t('nav.projects')}
              <svg
                className="nav-dropdown-chevron"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            <div className="nav-dropdown-menu">
              <NavLink
                to="/project/quantum-radar"
                className={({ isActive }) => isActive ? 'nav-dropdown-link active' : 'nav-dropdown-link'}
                onClick={closeMenu}
              >
                {t('nav.quantumRadar')}
              </NavLink>
              <NavLink
                to="/project/quantum-computer"
                className={({ isActive }) => isActive ? 'nav-dropdown-link active' : 'nav-dropdown-link'}
                onClick={closeMenu}
              >
                {t('nav.quantumComputer')}
              </NavLink>
              <NavLink
                to="/project/traffic-control"
                className={({ isActive }) => isActive ? 'nav-dropdown-link active' : 'nav-dropdown-link'}
                onClick={closeMenu}
              >
                {t('nav.trafficControl')}
              </NavLink>
            </div>
          </div>
        </nav>

        <div className="language-switcher" ref={langRef}>
          <button
            className="lang-trigger"
            onClick={() => setIsLangOpen(!isLangOpen)}
            aria-label="Sprache wählen"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
          </button>
          {isLangOpen && (
            <div className="lang-dropdown">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`lang-option ${currentLang === lang.code ? 'active' : ''}`}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <span className="lang-code">{lang.label}</span>
                  <span className="lang-name">{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="main-content">
        <Outlet />
      </main>

      <footer className="footer">
        <div className="footer-links">
          <button onClick={() => setModal('imprint')}>{t('imprint')}</button>
          <button onClick={() => setModal('privacy')}>{t('privacy')}</button>
        </div>
      </footer>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModal(null)}>×</button>
            <h2>{modal === 'imprint' ? t('imprint') : t('privacy')}</h2>
            <p>{modal === 'imprint' ? t('imprintText') : t('privacyText')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
